import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import PropTypes from 'prop-types'
import AuthenticationModal from './authentication'
import ConfirmationModal from './confirmation'
import { GeneralModal } from './general'
import { AUTH_INTENT_KINDS } from '../../authToggle/constants'
import { useApplicationContext } from '../context'

const ApplicationModalContext = createContext({
  open: () => {},
  update: () => {},
  close: () => {},
})

const applicationModalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_ACTIVE_MODAL': {
      return {
        ...state,
        activeModalId: action.id,
        modalProps: action.modalProps || {},
      }
    }
    case 'UPDATE_ACTIVE_MODAL': {
      return {
        ...state,
        modalProps: {
          ...state.modalProps,
          ...(action.modalProps || {}),
        },
      }
    }
    case 'CLOSE_ACTIVE_MODAL': {
      return {
        ...state,
        activeModalId: null,
      }
    }
    default: {
      return state
    }
  }
}

function ApplicationModalsProvider({ children }) {
  const [state, dispatch] = useReducer(applicationModalReducer, {
    // TODO: Find a way to enforce only extending from the default modal instances we define...
    modalInstances: {},
    active: null,
    modalProps: {},
  })

  const open = useCallback(
    (id, modalProps) => {
      dispatch({ type: 'OPEN_ACTIVE_MODAL', id, modalProps })
    },
    [dispatch]
  )

  const update = useCallback(
    modalProps => {
      dispatch({ type: 'UPDATE_ACTIVE_MODAL', modalProps })
    },
    [dispatch]
  )

  const close = useCallback(() => {
    if (!state.activeModalId) return

    const modal = state.modalInstances[state.activeModalId]

    // Optionally call this if the modal cares about it
    // TODO: how do we track the active mounted instances if only one should ever be alive on the page?
    modal?.props?.onClose?.()

    dispatch({ type: 'CLOSE_ACTIVE_MODAL' })
  }, [state.modalInstances, state.activeModalId, dispatch])

  const context = useMemo(
    () => ({
      open,
      update,
      close,
      modalIntents: AUTH_INTENT_KINDS,
    }),
    [close, open, update]
  )

  const { capabilities } = useApplicationContext()

  const getSelectedModal = () => {
    switch (state.activeModalId) {
      case 'authentication':
        if (!capabilities.authentication?.enabled) {
          return null
        }
        return <AuthenticationModal close={close} {...state.modalProps} />
      // TODO: Add future shared modals here
      case 'confirmation':
        if (!capabilities.confirmation?.enabled) {
          return null
        }
        return <ConfirmationModal close={close} {...state.modalProps} />
      case 'general':
        return <GeneralModal close={close} {...state.modalProps} />
      default:
        return null
    }
  }
  return (
    <ApplicationModalContext.Provider value={context}>
      {getSelectedModal()}
      {children}
    </ApplicationModalContext.Provider>
  )
}

ApplicationModalsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

const useApplicationModals = () => {
  const context = useContext(ApplicationModalContext)

  // TODO: Do something here if the context provider hasn't already been provided. We should have a standard pattern for
  // what that means which we don't have right now.

  return context
}

export {
  ApplicationModalContext,
  ApplicationModalsProvider,
  useApplicationModals,
}
